<template>
    <div class="substitutes-tree-view">
        <div v-if="!activeRoot" class="alert alert-info substitutes-tree-view__placeholder">
            Search for a part to view its substitute tree.
        </div>

        <div v-else>
            <div v-if="loading" class="text-center py-4">
                <div class="spinner-border text-danger" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>

            <div v-else-if="loadError" class="alert alert-danger" role="alert">
                {{ loadError }}
            </div>

            <div v-else>
                <div class="organization-chart">
                    <organization-chart :datasource="chartDatasource" @node-click="onNodeClick">
                        <template #default="{ nodeData }">
                            <div class="custom-node">
                                <div class="node-top part-number">
                                    <img
                                        v-if="nodeData.img"
                                        :src="nodeData.img"
                                        alt="avatar"
                                        class="node-avatar"
                                    />
                                    <div class="node-text">
                                        <div class="node-name" @click="copyCode(nodeData, $event)">
                                            {{ nodeData.name }}
                                        </div>
                                        <div
                                            v-if="getNodeSources(nodeData).length"
                                            class="node-vendors"
                                        >
                                            Vendor{{ getNodeSources(nodeData).length > 1 ? 's' : '' }}:
                                            {{ getNodeSources(nodeData).join(', ') }}
                                        </div>
                                        <div class="node-title" style="display: none">
                                            {{ nodeData.title }}
                                        </div>
                                    </div>
                                </div>

                                <div v-if="showStats" class="node-stats-wrapper">
                                    <div
                                        v-if="getStatsSummary(getNodeCode(nodeData))"
                                        class="node-stats"
                                    >
                                        <div class="node-stats__item">
                                            <span class="node-stats__label">Avg Quote</span>
                                            <span class="node-stats__value">
                                                {{
                                                    formatCurrency(
                                                        getStatsSummary(getNodeCode(nodeData))
                                                            .avg_quote
                                                    )
                                                }}
                                            </span>
                                        </div>
                                        <div class="node-stats__item">
                                            <span class="node-stats__label">Avg Price</span>
                                            <span class="node-stats__value">
                                                {{
                                                    formatCurrency(
                                                        getStatsSummary(getNodeCode(nodeData))
                                                            .avg_price
                                                    )
                                                }}
                                            </span>
                                        </div>
                                        <div class="node-stats__item">
                                            <span class="node-stats__label">Won %</span>
                                            <span class="node-stats__value">
                                                {{
                                                    formatPercent(
                                                        getStatsSummary(getNodeCode(nodeData))
                                                            .won_percent
                                                    )
                                                }}
                                            </span>
                                        </div>
                                        <div class="node-stats__item">
                                            <span class="node-stats__label">Quotes</span>
                                            <span class="node-stats__value">
                                                {{
                                                    formatCount(
                                                        getStatsSummary(getNodeCode(nodeData))
                                                            .quote_count
                                                    )
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                    <div v-else class="node-stats node-stats--empty">
                                        <span class="text-muted small">No stats available</span>
                                    </div>
                                </div>

                                <div class="node-actions">
                                    <div
                                        v-if="
                                            canRequestUnlink &&
                                            confirmingCode === getNodeCode(nodeData)
                                        "
                                        class="unlink-tooltip shadow-sm border rounded p-2 mt-2"
                                    >
                                        <p class="mb-2 text-center small">
                                            Request to unlink {{ nodeData.name }}
                                        </p>
                                        <div class="d-flex justify-content-center gap-2 mb-2">
                                            <textarea
                                                name="note"
                                                class="form-control note-textarea"
                                                placeholder="Additional information"
                                                v-model="note"
                                                rows="4"
                                                :disabled="sendingRequest"
                                            ></textarea>
                                        </div>
                                        <div class="d-flex justify-content-center gap-2">
                                            <button
                                                class="btn btn-sm btn-danger"
                                                @click.stop="confirmUnlink(nodeData)"
                                            >
                                                Yes
                                            </button>
                                            <button
                                                class="btn btn-sm btn-secondary"
                                                @click.stop="cancelUnlink"
                                            >
                                                No
                                            </button>
                                        </div>
                                    </div>
                                    <button
                                        class="btn btn-sm btn-primary me-2"
                                        @click.stop="copyCode(nodeData, $event)"
                                    >
                                        <font-awesome-icon :icon="['fas', 'copy']" />
                                    </button>
                                    <button
                                        v-if="showSelectButton"
                                        class="btn btn-sm btn-primary"
                                        @click.stop="selectNode(nodeData)"
                                    >
                                        <font-awesome-icon :icon="['fas', 'eye']" />
                                    </button>
                                    <button
                                        v-if="canRequestUnlink && !isRootNode(nodeData)"
                                        class="btn btn-sm btn-danger ms-2"
                                        @click.stop="unlinkNode(nodeData)"
                                    >
                                        <font-awesome-icon :icon="['fas', 'link-slash']" />
                                    </button>
                                </div>
                            </div>
                        </template>
                    </organization-chart>
                </div>

                <p v-if="!substitutes || substitutes.length === 0">No substitutes found.</p>
            </div>
        </div>
    </div>
</template>

<script>
    import OrganizationChart from 'vue3-organization-chart'
    import 'vue3-organization-chart/dist/orgchart.css'
    import { toast } from 'vue3-toastify'
    import { mapStores } from 'pinia'
    import config from '@/services/config'
    import { useSubstitutesStore } from '@/stores/substitutesStore'

    const currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
    })

    const integerFormatter = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
    })

    const normalizeTreePart = (value) => {
        if (value == null) return ''
        const normalized = value.toString().trim()
        if (!normalized) return ''
        if (normalized === '[]') return ''
        return normalized
    }

    const normalizeSources = (sources) => {
        if (!Array.isArray(sources)) return []
        return [...new Set(sources.map((source) => normalizeTreePart(source)).filter(Boolean))]
    }

    export default {
        name: 'SubstitutesTreeView',
        components: { OrganizationChart },
        props: {
            rootPart: {
                type: String,
                default: '',
            },
            partLabel: {
                type: String,
                default: '',
            },
            showSelectButton: {
                type: Boolean,
                default: true,
            },
            enableRemoveRequest: {
                type: Boolean,
                default: false,
            },
        },
        emits: ['select-substitute', 'remove-request'],
        data() {
            return {
                internalRootPart: '',
                statsFetchTimeout: null,
                confirmingCode: '',
                note: '',
                sendingRequest: false,
                merge_parts: true,
            }
        },
        computed: {
            ...mapStores(useSubstitutesStore),
            activeRoot() {
                return this.internalRootPart || this.partLabel || ''
            },
            loading() {
                const isLoading = this.substitutesStore.loading
                const tree = this.currentTree
                return Boolean(this.internalRootPart) && isLoading && !tree
            },
            loadError() {
                return this.substitutesStore.error
            },
            currentTree() {
                if (!this.internalRootPart) return null
                const getter = this.substitutesStore.substituteTreeByPart
                if (typeof getter !== 'function') return null
                return getter(this.internalRootPart) || null
            },
            processedTree() {
                const tree = this.currentTree
                if (!tree) return null
                return this.prepareTreeNode(tree)
            },
            substitutes() {
                const tree = this.processedTree
                if (!tree) return []
                return this.flattenTreeLeaves(tree)
            },
            chartDatasource() {
                const treeNode = this.processedTree || {
                    root: this.activeRoot || 'No substitutes loaded',
                    leaves: [],
                }
                return this.buildOrgChartTree(treeNode)
            },
            searchStatsData() {
                return this.searchStore.search?.statsData || {}
            },
            showStats() {
                return !config.isSearchDemo?.()
            },
            canRequestUnlink() {
                return this.enableRemoveRequest && !config.isSearchDemo()
            },
        },
        watch: {
            rootPart: {
                handler(value) {
                    const normalized = typeof value === 'string' ? value.trim() : ''
                    this.internalRootPart = normalized
                    this.queueStatsFetch()
                    this.confirmingCode = ''
                },
                immediate: true,
            },
            substitutes: {
                handler(newSubs) {
                    this.queueStatsFetch(newSubs)
                },
                immediate: true,
            },
            searchStatsData: {
                handler() {
                    this.queueStatsFetch()
                },
                deep: true,
            },
        },
        beforeUnmount() {
            if (this.statsFetchTimeout) {
                clearTimeout(this.statsFetchTimeout)
                this.statsFetchTimeout = null
            }
        },
        methods: {
            copyCode(item, evt) {
                const text = item?.code || item?.name || ''
                if (!text) return this.showToast('No code available to copy', 'error', evt)

                if (!navigator.clipboard)
                    return this.showToast('Clipboard unavailable in this browser', 'error', evt)

                navigator.clipboard
                    .writeText(text)
                    .then(() => this.showToast('Part code copied to clipboard!', 'default'))
                    .catch(() => this.showToast('Failed to copy code', 'error', evt))
            },
            showToast(message, type) {
                toast(message, {
                    type,
                    pauseOnFocusLoss: false,
                })
            },
            selectNode(nodeData) {
                if (!this.showSelectButton) return
                const item = {
                    name: nodeData.name,
                    code: this.getNodeCode(nodeData),
                    title: nodeData.title,
                }
                this.$emit('select-substitute', item)
            },
            unlinkNode(nodeData) {
                const code = this.getNodeCode(nodeData)
                if (!code) return
                this.confirmingCode = this.confirmingCode === code ? '' : code
            },
            async confirmUnlink(nodeData) {
                this.sendingRequest = true
                const substituteCode = this.getNodeCode(nodeData)
                const rootValue = (this.internalRootPart || this.partLabel || '').toString().trim()
                if (!substituteCode || !rootValue) {
                    this.confirmingCode = ''
                    this.note = ''
                    return
                }

                const requestPayload = {
                    substitute_part: substituteCode,
                    note: this.note || '',
                    part_number: rootValue,
                }

                try {
                    const response =
                        await this.substitutesStore.requestSubstituteRemoval(requestPayload)
                    this.showToast('Removal request sent', 'success')
                    this.$emit('remove-request', { response, nodeData, payload: requestPayload })
                } catch (error) {
                    console.error('Failed to request substitute removal', error)
                    this.showToast('Failed to send removal request', 'error')
                } finally {
                    this.note = ''
                    this.confirmingCode = ''
                    this.sendingRequest = false
                }
            },
            cancelUnlink() {
                this.confirmingCode = ''
                this.note = ''
            },
            getNodeCode(nodeData) {
                if (!nodeData || typeof nodeData !== 'object') return ''
                const raw = nodeData.code || nodeData.name || ''
                return typeof raw === 'string' ? raw.trim() : ''
            },
            isRootNode(nodeData) {
                const code = this.getNodeCode(nodeData)
                if (!code) return false
                const rootValue = (this.internalRootPart || this.partLabel || '').toString().trim()
                if (!rootValue) return false
                return code === rootValue
            },
            onNodeClick(nodeData) {
                console.log('node clicked', nodeData)
            },
            getNodeSources(nodeData) {
                if (!nodeData || typeof nodeData !== 'object') return []
                return normalizeSources(nodeData.sources)
            },
            prepareTreeNode(treeNode) {
                if (!treeNode || typeof treeNode !== 'object') {
                    return {
                        root: this.activeRoot || '',
                        leaves: [],
                    }
                }

                const root = normalizeTreePart(treeNode.root || this.activeRoot)
                const leaves = Array.isArray(treeNode.leaves) ? treeNode.leaves : []

                return {
                    ...treeNode,
                    root,
                    leaves: this.merge_parts
                        ? this.mergeTreeLeaves(leaves)
                        : this.normalizeLeaves(leaves),
                }
            },
            normalizeLeaves(leaves = []) {
                return leaves.map((leaf) => this.normalizeLeafNode(leaf)).filter(Boolean)
            },
            normalizeLeafNode(leaf) {
                if (!leaf || typeof leaf !== 'object') return null
                const partNumber = normalizeTreePart(leaf.substitute_part)
                const sources = normalizeSources(leaf.sources)
                const children = this.normalizeLeaves(Array.isArray(leaf.leaves) ? leaf.leaves : [])

                return {
                    ...leaf,
                    substitute_part: partNumber,
                    sources,
                    leaves: children,
                }
            },
            mergeTreeLeaves(leaves = []) {
                const mergedByPart = new Map()
                const passthrough = []

                for (let index = 0; index < leaves.length; index += 1) {
                    const leaf = this.normalizeLeafNode(leaves[index])
                    if (!leaf) continue

                    const partNumber = leaf.substitute_part
                    if (!partNumber) {
                        if (leaf.leaves.length) {
                            passthrough.push(...this.mergeTreeLeaves(leaf.leaves))
                        }
                        continue
                    }

                    const existing = mergedByPart.get(partNumber)
                    if (!existing) {
                        mergedByPart.set(partNumber, {
                            ...leaf,
                            sources: [...leaf.sources],
                            leaves: [...leaf.leaves],
                        })
                        continue
                    }

                    existing.sources = [...new Set([...existing.sources, ...leaf.sources])]
                    existing.leaves = this.mergeTreeLeaves([...existing.leaves, ...leaf.leaves])
                }

                return [...mergedByPart.values(), ...passthrough]
            },
            buildOrgChartTree(treeNode) {
                const rootPart = normalizeTreePart(treeNode?.root || this.activeRoot)
                return {
                    id: `root-${rootPart}`,
                    name: rootPart,
                    title: 'Root Part',
                    sources: [],
                    children: (treeNode?.leaves || [])
                        .filter((leaf) => {
                            const partNumber = normalizeTreePart(leaf?.substitute_part)
                            return partNumber && partNumber !== rootPart
                        })
                        .map((leaf, index) => this.buildOrgChartLeaf(leaf, `leaf-${index}`)),
                }
            },
            buildOrgChartLeaf(leaf, id) {
                if (!leaf) {
                    return null
                }

                const partNumber = normalizeTreePart(leaf?.substitute_part)
                if (!partNumber) {
                    return null
                }

                const sources = normalizeSources(leaf.sources)
                return {
                    id,
                    name: partNumber,
                    sources,
                    title: sources.length ? `Sources: ${sources.join(', ')}` : 'Substitute',
                    children: (leaf.leaves || [])
                        .map((child, idx) => this.buildOrgChartLeaf(child, `${id}-${idx}`))
                        .filter(Boolean),
                }
            },
            flattenTreeLeaves(treeNode) {
                if (!treeNode || !Array.isArray(treeNode.leaves)) {
                    return []
                }

                const flattened = []
                const queue = [...treeNode.leaves]

                while (queue.length > 0) {
                    const node = queue.shift()
                    if (!node) {
                        continue
                    }

                    const partNumber = normalizeTreePart(node.substitute_part)
                    if (!partNumber) {
                        if (Array.isArray(node.leaves) && node.leaves.length) {
                            queue.push(...node.leaves)
                        }
                        continue
                    }

                    const sources = normalizeSources(node.sources)
                    flattened.push({
                        name: partNumber,
                        sources,
                        note: sources.length ? `Sources: ${sources.join(', ')}` : '',
                    })

                    if (Array.isArray(node.leaves) && node.leaves.length) {
                        queue.push(...node.leaves)
                    }
                }

                return flattened
            },
            queueStatsFetch(subs = null) {
                if (this.statsFetchTimeout) {
                    clearTimeout(this.statsFetchTimeout)
                    this.statsFetchTimeout = null
                }
                if (!this.showStats) {
                    return
                }

                const codes = new Set()
                const rootValue = (this.internalRootPart || this.partLabel || '').toString().trim()
                if (rootValue) {
                    codes.add(rootValue)
                }

                const items = Array.isArray(subs) ? subs : this.substitutes
                for (let index = 0; index < items.length; index += 1) {
                    const entry = items[index]
                    let candidate = ''

                    if (typeof entry === 'string') {
                        candidate = entry
                    } else if (entry && typeof entry === 'object') {
                        const rawValue =
                            entry.part_number ||
                            entry.part ||
                            entry.substitute_part ||
                            entry.code ||
                            entry.name ||
                            ''
                        candidate = rawValue
                    }

                    if (typeof candidate === 'string') {
                        const cleaned = candidate.trim()
                        if (cleaned.length) {
                            codes.add(cleaned)
                        }
                    }
                }

                if (!codes.size) {
                    return
                }

                this.statsFetchTimeout = setTimeout(() => {
                    this.fetchStatsForParts(Array.from(codes))
                    this.statsFetchTimeout = null
                }, 0)
            },
            fetchStatsForParts(parts = []) {
                if (!this.showStats) return
                if (!Array.isArray(parts) || !parts.length) return
                this.substitutesStore.fetchSubstituteStats({
                    parts,
                    statsData: this.searchStatsData,
                    vuexStore: this.searchStore,
                })
            },
            getStatsForPart(code) {
                if (!code) return null
                const getter = this.substitutesStore.substituteStatsByPart
                if (typeof getter !== 'function') return null
                return getter(code) || null
            },
            getStatsSummary(code) {
                const stats = this.getStatsForPart(code)
                return stats?.summary || null
            },
            formatCurrency(value) {
                if (!Number.isFinite(value)) return '$0.00'
                return currencyFormatter.format(value)
            },
            formatPercent(value) {
                if (!Number.isFinite(value)) return '0%'
                return `${value.toFixed(1)}%`
            },
            formatCount(value) {
                if (!Number.isFinite(value)) return '0'
                return integerFormatter.format(value)
            },
        },
    }
</script>

<style scoped lang="scss">
    $chart-color: var(--color-border-accent-strong);

    .substitutes-tree-view__placeholder {
        background: var(--color-surface-subtle);
        border: 1px dashed var(--color-border-accent-soft);
        color: var(--color-text-secondary);
    }

    .organization-chart {
        text-align: center;

        :deep(.chartOrgchart),
        :deep(.chartOrgchartContainer),
        :deep(.chartOrgchart table),
        :deep(.chartOrgchart tbody),
        :deep(.chartOrgchart tr),
        :deep(.chartOrgchart td),
        :deep(.chartLines) {
            background: var(--color-surface-canvas) !important;
        }

        :deep(table) {
            width: auto !important;
            margin: 0 !important;
            border: none !important;
            background: var(--color-surface-canvas) !important;
            border-collapse: collapse !important;

            tbody,
            tr,
            td,
            th {
                background: var(--color-surface-canvas) !important;
            }

            td,
            th {
                border: none !important;
                padding: 0 !important;
                text-align: center !important;
                background: var(--color-surface-canvas) !important;
            }
            tr {
                &:nth-child(even) {
                    background-color: var(--color-surface-canvas) !important;
                }

                &:hover {
                    background-color: var(--color-surface-canvas) !important;
                }
            }

            .chartDownLine {
                background: $chart-color;
            }
            .chartRightLine {
                border-right-color: $chart-color !important;
                border-right-style: solid !important;
                border-right-width: 2px !important;
            }
            .chartTopLine {
                border-top-color: $chart-color !important;
                border-top-style: solid !important;
                border-top-width: 2px !important;
            }
            .chartLeftLine {
                border-left-color: $chart-color !important;
                border-left-style: solid !important;
                border-left-width: 1px !important;
            }

            .chartNode {
                border: 1px solid $chart-color;
                min-width: 11rem !important;
                &:hover {
                    box-shadow: 0 0 5px $chart-color;
                }
                .chartTitle {
                    background: $chart-color;
                }
            }
        }

        :deep(.node) {
            background: none !important;
            border: none !important;
        }
    }
    :deep(.chartOrgchartContainer) {
        max-width: 100%;
        border: none;

        .chartNode {
            min-width: 165px;
        }
    }
    :deep(.substitute-modal-toast) {
        background: var(--color-surface-inverse);
        color: var(--color-text-inverse);
        font-weight: 500;
        border-radius: 6px;
        box-shadow: var(--shadow-md);
    }
    .custom-node {
        display: inline-block;
        width: 100%;
        padding: 8px;
        border-radius: 6px;
        border: 1px solid var(--color-border-default);
        background: var(--color-surface-canvas);
        box-shadow: var(--shadow-xs);
        text-align: left;
        color: var(--color-text-primary);

        .node-top {
            display: flex;
            align-items: center;

            .node-avatar {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                object-fit: cover;
                margin-right: 8px;
                background: var(--color-surface-subtle);
                border: 1px solid var(--color-border-muted);
            }
            .node-text {
                .node-name {
                    font-weight: 600;
                    font-size: 0.95rem;
                }
                .node-vendors {
                    font-size: 0.74rem;
                    color: var(--color-text-secondary);
                    margin-top: 2px;
                    line-height: 1.25;
                }
                .node-title {
                    font-size: 0.82rem;
                    color: var(--color-text-muted);
                }
                .node-code {
                    font-size: 0.78rem;
                    color: var(--color-text-subtle);
                    margin-top: 4px;
                }
            }
        }
        .node-actions {
            margin-top: 8px;
            text-align: center;

            .btn {
                min-width: 32px;
            }
        }
        .node-stats-wrapper {
            margin-top: 8px;
        }
        .node-stats {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 6px;
            padding: 6px;
            border-radius: 4px;
            background: var(--color-surface-subtle);
            border: 1px solid var(--color-border-muted);
        }
        .node-stats__item {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }
        .node-stats__label {
            font-size: 10px;
            color: var(--color-text-muted);
            text-transform: uppercase;
        }
        .node-stats__value {
            font-size: 12px;
            font-weight: 600;
            color: var(--color-text-primary);
        }
        .node-stats--empty {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 8px 0;
        }
    }
    .unlink-tooltip {
        max-width: 200px;
        width: 151px;
        position: absolute;
        z-index: 21;
        top: 0;
        left: 50%;
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
        border-color: var(--color-border-default) !important;
        box-shadow: var(--shadow-lg) !important;
        transform: translateX(-50%);
    }
    .note-textarea {
        font-size: 12px;
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
        border-color: var(--color-border-default);
    }
    .note-textarea::placeholder {
        color: var(--color-text-muted);
    }
    .part-number {
        word-break: break-all;
        white-space: normal;
    }
</style>
